<template>
  <main class="main page-search">
    <div class="main_wrap">
      <div class="f fjb mb32 fw gap16">
        <PagesItemComponentsChildrenTitle
          :name="t('weapon_page_title')"
          class="mb0"
        />
        <div class="f gap16 hlgt">
          <button
            :class="{ compressed, p8: compressed, p3: !compressed }"
            aria-label="compressed button"
            class="btn btn-primary ht hd wh40"
            @click="toggleCompressed"
          >
            <svg>
              <use
                v-if="compressed"
                xlink:href="/icons/sprite.svg#grid-1"
              />
              <use
                v-else
                xlink:href="/icons/sprite.svg#grid-2"
              />
            </svg>
          </button>
          <button
            class="btn btn-primary"
            @click="openAdvancedSearch"
          >
            <i class="i-filter f20" />
            <span>{{ t("filters.filters") }}</span>
          </button>
        </div>
      </div>

      <div class="items-wrapper">
        <div class="filters-wrapper hlgd">
          <div class="f mb16 filters-header">
            <p class="fw600">
              {{ t("filters.filters") }}
            </p>
            <button
              class="btn btn-primary"
              @click="clearFilters"
            >
              {{ t("clear") }}
            </button>
          </div>
          <PagesSearchComponentsFilters
            v-model="filters"
            @on-filters-change="onFiltersChange"
          />
        </div>
        <PagesSearch
          :items="items"
          :pagination="{ page, pagesLength: maxPages }"
          :pending="pending"
          @on-page-change="onPageChange"
        />
      </div>
    </div>
  </main>
</template>

<script lang="ts" setup>
import type { LocationQuery, LocationQueryValue } from 'vue-router';
import type { AdvancedSearchBody, AdvancedSearchInput } from '~/graphql/generated/schema-types';
import { useRuntimeConfig } from '#app';
import { useAlternateLinks } from '#imports';
import { EventBus } from '~/composables/bus';
import { useCompressed } from '~/composables/items-compressed';
import { OrderBy } from '~/graphql/generated/schema-types';
import { useStore } from '~/store';
import { useModalStore } from '~/store/modal';
import { ModalIds } from '~/types/Modals';
import { advancedSearch } from '~/utils/api';

const route = useRoute();
const router = useRouter();
const { gtag } = useGtag();
const debounce = useDebounce();
const store = useStore();

const { locale, t } = useI18n();
const localePath = useLocalePath();

const config = useRuntimeConfig();
const siteHost = config.public.siteHost;

const currency = computed(() => store.rate);
const page = ref(+(route.query.page || 1));
const maxPages = ref(1);
const { compressed, toggleCompressed } = useCompressed();
const pending = ref(false);
const items = ref([] as AdvancedSearchBody[]);
const filters = ref<AdvancedSearchInput>({} as AdvancedSearchInput);
const oldPage = ref(+(route.query.page || 1));

const modalStore = useModalStore();

function openAdvancedSearch() {
  modalStore.openModal(ModalIds.ADVANCED_SEARCH);
}

async function getItems(filters: LocationQuery) {
  pending.value = true;
  if (!('orderBy' in filters)) {
    filters.orderBy = OrderBy.Popularity;
  }

  try {
    delete filters.page;
    const { data, errors } = await advancedSearch(
      parseQuery(filters) as AdvancedSearchInput,
      currency.value,
      page.value,
    );
    if (errors?.length) {
      throw new Error(errors[0]?.message);
    }

    return {
      maxPages: data.advancedSearch.max_pages,
      items: data.advancedSearch.result as AdvancedSearchBody[],
    };
  } catch (e: any) {
    console.error('advancedSearch error ', e.message);
    EventBus.$emit('notify', {
      success: false,
      msg: e.message || 'Something went wrong. Please try again later.',
    });
    return {
      maxPages: 0,
      items: [] as AdvancedSearchBody[],
    };
  } finally {
    pending.value = false;
  }
}

const { data, error } = await useAsyncData('getAdvancedSearchItems', () => getItems(route.query));

if (error.value) {
  if (process.client) {
    window.location.reload();
  }
  console.log(`Search page fetch error: ${error.value?.message}` || 'Not found');
  throw createError({
    status: 404,
    statusText: `Search page fetch error: ${error.value?.message}` || 'Not found',
  });
}

items.value = data.value?.items || [];
maxPages.value = data.value?.maxPages || 0;

watch(
  [toRef(route, 'query'), currency],
  async ([filtersArg]) => {
    if (+(filtersArg.page || '1') === oldPage.value && filtersArg.page !== '1') {
      await onPageChange(1);
      return;
    } else {
      oldPage.value = +(filtersArg.page || 1);
    }
    filters.value = { ...filtersArg };

    const res = await getItems(filtersArg);
    items.value = res.items || [];
    maxPages.value = res.maxPages || 0;
  },
  { deep: true },
);

async function onPageChange(newPage: number) {
  page.value = newPage;
  await router.push({ query: { ...route.query, page: newPage } });
  window.scrollTo(0, 0);
}

function parseQuery(
  query: Record<string, LocationQueryValue | LocationQueryValue[]>,
): Record<string, any> {
  const parsedParams: Record<string, any> = {};

  for (const key in query) {
    if (Object.prototype.hasOwnProperty.call(query, key)) {
      const value = query[key];
      if (!value)
        continue;

      if (Array.isArray(value)) {
        // Если значение - массив, преобразуем каждый элемент
        parsedParams[key] = value.map(v => (Number.isNaN(Number(v)) ? v : Number(v)));
      } else {
        // Если значение можно преобразовать в число, делаем это, иначе оставляем как строку
        parsedParams[key] = Number.isNaN(Number(value)) ? value : Number(value);
      }
    }
  }

  return parsedParams;
}

onBeforeUnmount(() => {
  router.replace({ query: {} });
});

function filterEmptyValues(obj: AdvancedSearchInput) {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([_, value]) => value !== null && value !== undefined && value !== '',
    ),
  );
}

function clearFilters() {
  filters.value = {} as AdvancedSearchInput;
}

function sendGtag() {
  if (!filters.value.searchText)
    return;

  gtag('event', 'csmarket-search', {
    search_term: filters.value.searchText,
    event_label: 'Advanced search',
  });
}

const submit = debounce(() => {
  sendGtag();
  router.push({ path: localePath('/search'), query: filterEmptyValues(filters.value) });
}, 500);

async function onFiltersChange() {
  await nextTick();
  page.value = 1;
  submit();
}

const { alternateLinks, canonical } = useAlternateLinks('/search');

useHead({
  meta: [
    {
      name: 'keywords',
      content: 'steam, counter strike global offensive, csgo, cs2, skins, items',
    },
  ],
  link: () => alternateLinks.value,
});

useSeoMeta({
  title: () => t('heads.search.title'),
  ogTitle: () => t('heads.search.title'),
  description: () => t('heads.homepage.description'),
  ogDescription: () => t('heads.homepage.description'),
  ogImage: () => 'https://csmarketcap.com/img/logo-bg-black.png',
  twitterCard: 'summary_large_image',
  twitterTitle: () => t('heads.search.title'),
  twitterDescription: () => t('heads.homepage.description'),
  twitterImage: 'https://csmarketcap.com/img/logo-bg-black.png',
  ogSiteName: 'CSMarketCap',
  ogUrl: () => canonical.value,
  ogType: 'website',
  ogLocale: () => locale.value,
});

useSchemaOrg([
  defineWebPage({
    '@type': 'SearchResultsPage',
    'name': t('heads.search.title'),
    'description': t('heads.homepage.description'),
    'url': `${siteHost}/search`,
    'image': 'https://csmarketcap.com/img/logo-bg-black.png',
    'publisher': {
      '@type': 'Organization',
      'name': 'CSMarketCap',
      'logo': {
        '@type': 'ImageObject',
        'url': 'https://csmarketcap.com/img/logo-bg-black.png',
      },
    },
  }),
  {
    '@type': 'ItemList',
    'itemListElement': (data.value?.items || []).map((item, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'item': {
        '@type': 'Product',
        'name': item?.localed_name || item?.market_hash_name || '',
        'description': [item?.localed_skin || '', item?.localed_rarity || '']
          .filter(Boolean)
          .join(' '),
        'image': getImageFromWaxpeer(`${siteHost}/${item?.link || ''}`),
        'url': localePath(
          getItemLinkWithoutExt({
            name: item?.market_hash_name,
            slug: item?.link,
          }),
        ),
        'aggregateRating': {
          '@type': 'AggregateRating',
          'ratingValue': item?.rating?.toString() || '0',
          'ratingCount': item?.votes || 0,
        },
      },
    })),
  },
]);
</script>

<style lang="scss" scoped>
.page-search {
  padding: 18rem 0 4rem;

  &:before {
    content: unset !important;
  }

  .filters-header {
    border-bottom: 1px solid var(--surfaces-high-container);
    padding-bottom: 16px;

    .fw600 {
      font-size: 18px;
    }
  }

  .main_wrap {
    padding-left: 37rem;
  }

  .items-wrapper {
    display: flex;
    gap: 16px;

    &:deep(.weapon_wrap) {
      flex-grow: 1;
      //overflow-y: auto;
      //height: calc(100vh - 17.5rem);
    }

    .filters-wrapper {
      width: 32rem;
      background: var(--surfaces-low-container);
      overflow-y: auto;
      border-radius: 1.6rem;

      position: fixed;
      top: 14.5rem;
      left: 0;
      bottom: 0;
      padding: 2rem;
      margin: 2rem;
    }
  }

  @media screen and (max-width: 1115px) {
    padding: 8rem 20px 4rem;

    .main_wrap {
      padding-left: 0;
    }

    .items-wrapper {
      display: block;
    }
  }

  @media screen and (max-width: 768px) {
    padding: 8rem 16px 4rem;
  }
}
</style>
